import { InfoscreenFeed } from '../infoscreen-module.types';

/** How long a demo screen shows each set-up. */
export const DEMO_SECONDS = 30;

/** One set-up a demo screen shows: what it is called on the screen, and what it changes in the feed. */
export interface DemoPreset {
  label: string;
  changes: Partial<InfoscreenFeed>;
}

const PLAIN = { showDecorations: false, decorAtTop: false, showSun: false } satisfies Partial<InfoscreenFeed>;
const SEASON = { showDecorations: true, decorAtTop: false, showSun: true } satisfies Partial<InfoscreenFeed>;

/**
 * The set-ups a demo screen goes through, in this order: the plain themes, the ones that follow the
 * sun and the year, then the seasons and the holidays in the year's order — with the sky, the
 * decorations along the edges or only at the top, and the welcome pane on either side between them.
 */
export const DEMO_PRESETS: DemoPreset[] = [
  { label: 'Klassisk', changes: { ...PLAIN, colorTheme: 'Classic', splitSide: 'None' } },
  { label: 'Klassisk med velkomst', changes: { ...PLAIN, colorTheme: 'Classic', splitSide: 'Right' } },
  { label: 'Mørk', changes: { ...PLAIN, colorTheme: 'Dark', splitSide: 'None' } },
  { label: 'Lys', changes: { ...PLAIN, colorTheme: 'Light', splitSide: 'Left' } },
  { label: 'Følger solen', changes: { ...PLAIN, colorTheme: 'DayCycleClassic', showSun: true, splitSide: 'Right' } },
  { label: 'Lys dag, mørk nat', changes: { ...PLAIN, colorTheme: 'DayCycleLightDark', showSun: true, splitSide: 'None' } },
  { label: 'Følger året', changes: { ...SEASON, colorTheme: 'DayCycleYear', splitSide: 'Right' } },
  { label: 'Vinter', changes: { ...SEASON, colorTheme: 'Winter', splitSide: 'None' } },
  { label: 'Kinesisk nytår', changes: { ...SEASON, colorTheme: 'LunarNewYear', splitSide: 'Right' } },
  { label: 'Fastelavn', changes: { ...SEASON, colorTheme: 'Fastelavn', splitSide: 'None' } },
  { label: 'Forår, pynt i toppen', changes: { ...SEASON, colorTheme: 'Spring', decorAtTop: true, splitSide: 'Left' } },
  { label: 'Påske', changes: { ...SEASON, colorTheme: 'Easter', splitSide: 'Right' } },
  { label: 'Eid', changes: { ...SEASON, colorTheme: 'Eid', splitSide: 'None' } },
  { label: 'Sommer', changes: { ...SEASON, colorTheme: 'Summer', splitSide: 'Right' } },
  { label: 'Sankt Hans', changes: { ...SEASON, colorTheme: 'SanktHans', splitSide: 'None' } },
  { label: 'Efterår, pynt i toppen', changes: { ...SEASON, colorTheme: 'Autumn', decorAtTop: true, splitSide: 'Right' } },
  { label: 'Halloween', changes: { ...SEASON, colorTheme: 'Halloween', splitSide: 'None' } },
  { label: 'Diwali', changes: { ...SEASON, colorTheme: 'Diwali', splitSide: 'Left' } },
  { label: 'Jul', changes: { ...SEASON, colorTheme: 'Christmas', splitSide: 'Right' } },
  { label: 'Nytår', changes: { ...SEASON, colorTheme: 'NewYear', splitSide: 'None' } },
];

/**
 * The preset a demo screen shows at `now`: by the clock, so screens side by side change together.
 * In a rotating demo (`?screenIds=`) the screen is on only every so often, so there it shows preset
 * `turn` — the next one each time its turn comes — and every preset gets its turn. Null for a
 * screen that is not a demo.
 */
export function demoPreset(feed: InfoscreenFeed | null, now: Date, turn: number | null = null): DemoPreset | null {
  // The one-room views have no theme or layout to go through.
  if (!feed?.demo || feed.viewType === 'Room' || feed.viewType === 'LockerRoom') return null;
  const index = turn ?? Math.floor(now.getTime() / (DEMO_SECONDS * 1000));
  return DEMO_PRESETS[index % DEMO_PRESETS.length];
}

/** Seconds the preset shown at `now` has been on the screen. */
export function demoElapsed(now: Date): number {
  return (now.getTime() % (DEMO_SECONDS * 1000)) / 1000;
}

/**
 * The feed as the preset shows it: its theme and layout, the screen's own bookings, ads and news.
 * A split on a view without room to share is left to `ScreenHostComponent.split()`, which ignores it.
 */
export function withPreset(feed: InfoscreenFeed, preset: DemoPreset): InfoscreenFeed {
  return { ...feed, ...preset.changes, hiddenHolidays: [] };
}
